import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, X, Loader2, AlertCircle, TrendingUp, Zap, Image as ImageIcon,
  Type as TypeIcon, Lightbulb, Lock, CheckCircle2,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { generateVideoThumbnails } from '../lib/videoThumbnails';

export type AiUsage = {
  used: number;
  limit: number;
  remaining: number;
  resetAt: string;
  hasSubscription: boolean;
  planName: string | null;
};

export type AiPrediction = {
  mediaScore: number;
  captionScore: number;
  overallScore: number;
  verdict: string;
  summary: string;
  improvements: string[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  caption: string;
  /** Selected media in the composer (id + url + kind). Videos will be sampled into thumbnails on the client. */
  media: Array<{ id: string; url: string; kind: 'image' | 'video' }>;
};

export function AiPredictionModal({ open, onClose, caption, media }: Props) {
  const [phase, setPhase] = useState<'idle' | 'prepping' | 'predicting' | 'result' | 'error'>('idle');
  const [usage, setUsage] = useState<AiUsage | null>(null);
  const [prediction, setPrediction] = useState<AiPrediction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // Reset state when reopening
  useEffect(() => {
    if (!open) return;
    setPhase('idle');
    setPrediction(null);
    setError(null);
    setErrorCode(null);
    // Fetch usage on open
    api<{ usage: AiUsage }>('/api/ai/usage')
      .then((r) => setUsage(r.usage))
      .catch(() => setUsage(null));
  }, [open]);

  // Lock body scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  async function run() {
    setError(null);
    setErrorCode(null);
    setPrediction(null);
    try {
      setPhase('prepping');
      const imageIds: string[] = [];
      const videos: Array<{ url: string }> = [];
      for (const m of media) {
        if (m.kind === 'image') imageIds.push(m.id);
        else videos.push({ url: m.url });
      }
      // Generate up to 3 frames per video, capped to keep payload small.
      const extraImages: string[] = [];
      for (const v of videos.slice(0, 2)) {
        try {
          const frames = await generateVideoThumbnails(v.url, { count: 3, maxSide: 640, quality: 0.78 });
          extraImages.push(...frames);
          if (extraImages.length >= 4) break;
        } catch {
          // Continue with whatever we have
        }
      }

      setPhase('predicting');
      const res = await api<{ id: string; prediction: AiPrediction; usage: AiUsage }>(
        '/api/ai/predict',
        {
          method: 'POST',
          body: JSON.stringify({
            caption,
            mediaIds: imageIds.slice(0, 4),
            extraImages: extraImages.slice(0, 4),
          }),
        },
      );
      setPrediction(res.prediction);
      setUsage(res.usage);
      setPhase('result');
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorCode(err.code);
        const m = (err as ApiError & { details?: { message?: string } }).details?.message;
        setError(m || err.message || 'Algo ha fallado.');
      } else {
        setError(err instanceof Error ? err.message : 'Algo ha fallado.');
      }
      setPhase('error');
    }
  }

  if (!open) return null;

  const canRun = (caption.trim().length > 0 || media.length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 transition"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-brand-500 to-purple-600 flex items-center justify-center text-white shadow-lg shadow-brand-500/20">
              <Sparkles size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Predicción de rendimiento</h2>
              <p className="text-xs text-slate-500">Analizamos tu post con IA antes de publicarlo.</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* IDLE — pre-run */}
          {phase === 'idle' && (
            <PreRunState
              usage={usage}
              canRun={canRun}
              caption={caption}
              mediaCount={media.length}
              onRun={run}
              onClose={onClose}
            />
          )}

          {(phase === 'prepping' || phase === 'predicting') && (
            <RunningState phase={phase} />
          )}

          {phase === 'error' && (
            <ErrorState
              code={errorCode}
              message={error ?? 'Error desconocido'}
              usage={usage}
              onRetry={canRun ? run : null}
              onClose={onClose}
            />
          )}

          {phase === 'result' && prediction && (
            <ResultState prediction={prediction} usage={usage} onRetry={run} onClose={onClose} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── States ─────────────────────────── */

function PreRunState({ usage, canRun, caption, mediaCount, onRun, onClose }: {
  usage: AiUsage | null;
  canRun: boolean;
  caption: string;
  mediaCount: number;
  onRun: () => void;
  onClose: () => void;
}) {
  if (usage && !usage.hasSubscription) {
    return <UpgradeCta title="Necesitas una suscripción activa" subtitle="Predicción IA está disponible con cualquier plan de Zettapost." onClose={onClose} />;
  }
  if (usage && usage.limit <= 0) {
    return <UpgradeCta title="Tu plan no incluye Predicción IA" subtitle="Cambia a un plan superior para usar esta función." onClose={onClose} />;
  }
  if (usage && usage.remaining <= 0) {
    return <UpgradeCta
      title={`Has usado ${usage.used}/${usage.limit} predicciones este mes`}
      subtitle="Mejora tu plan para tener más predicciones disponibles."
      onClose={onClose}
    />;
  }

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-5">
        <SummaryCard icon={<TypeIcon size={14} />} label="Texto" value={`${caption.trim().length} caract.`} />
        <SummaryCard icon={<ImageIcon size={14} />} label="Media" value={`${mediaCount} archivo${mediaCount === 1 ? '' : 's'}`} />
      </div>

      <ul className="space-y-2 mb-6 text-sm text-slate-600">
        <BulletItem>Puntuamos tu media (composición, atención).</BulletItem>
        <BulletItem>Puntuamos tu texto (hook, claridad, CTA).</BulletItem>
        <BulletItem>5+ consejos accionables para mejorar el post.</BulletItem>
      </ul>

      {usage && (
        <UsagePill usage={usage} className="mb-4" />
      )}

      <button
        onClick={onRun}
        disabled={!canRun}
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-br from-brand-500 to-purple-600 text-white font-semibold shadow-lg shadow-brand-500/20 hover:shadow-xl hover:shadow-brand-500/30 hover:from-brand-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
      >
        <Sparkles size={16} /> Analizar con IA
      </button>
      {!canRun && (
        <p className="mt-2 text-xs text-center text-slate-500">Añade un texto o una imagen para poder analizar.</p>
      )}
    </div>
  );
}

function RunningState({ phase }: { phase: 'prepping' | 'predicting' }) {
  return (
    <div className="py-10 flex flex-col items-center justify-center text-center">
      <div className="relative mb-6">
        <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-brand-500 to-purple-600 flex items-center justify-center text-white shadow-xl shadow-brand-500/30">
          <Sparkles size={26} className="animate-pulse" />
        </div>
        <div className="absolute -inset-2 rounded-3xl bg-gradient-to-br from-brand-400/30 to-purple-500/30 blur-xl animate-pulse" />
      </div>
      <div className="flex items-center gap-2 text-slate-700 font-medium">
        <Loader2 size={16} className="animate-spin" />
        {phase === 'prepping' ? 'Preparando media…' : 'Analizando con IA…'}
      </div>
      <p className="mt-2 text-xs text-slate-500 max-w-xs">
        {phase === 'prepping'
          ? 'Generando miniaturas de los vídeos para enviarlos a la IA.'
          : 'Esto puede tardar entre 5 y 20 segundos. No cierres esta ventana.'}
      </p>
    </div>
  );
}

function ErrorState({ code, message, usage, onRetry, onClose }: {
  code: string | null;
  message: string;
  usage: AiUsage | null;
  onRetry: (() => void) | null;
  onClose: () => void;
}) {
  if (code === 'SUBSCRIPTION_REQUIRED' || code === 'NOT_INCLUDED_IN_PLAN') {
    return <UpgradeCta title={code === 'SUBSCRIPTION_REQUIRED' ? 'Suscripción requerida' : 'No incluido en tu plan'} subtitle={message} onClose={onClose} />;
  }
  if (code === 'LIMIT_REACHED') {
    return <UpgradeCta title="Límite mensual alcanzado" subtitle={message} onClose={onClose} />;
  }
  return (
    <div className="text-center py-6">
      <div className="mx-auto h-12 w-12 rounded-full bg-red-100 flex items-center justify-center text-red-600 mb-3">
        <AlertCircle size={22} />
      </div>
      <h3 className="font-semibold text-slate-900">No pudimos analizar el post</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{message}</p>
      <div className="mt-5 flex items-center justify-center gap-2">
        {onRetry && (
          <button onClick={onRetry} className="btn-primary">Volver a intentar</button>
        )}
        <button onClick={onClose} className="btn-secondary">Cerrar</button>
      </div>
      {usage && <UsagePill usage={usage} className="mt-5" />}
    </div>
  );
}

function ResultState({ prediction, usage, onRetry, onClose }: {
  prediction: AiPrediction;
  usage: AiUsage | null;
  onRetry: () => void;
  onClose: () => void;
}) {
  const tone = scoreTone(prediction.overallScore);
  return (
    <div>
      {/* Hero score */}
      <div className={`rounded-2xl p-5 mb-5 ${tone.bg} ${tone.border} border`}>
        <div className="flex items-center gap-4">
          <ScoreRing value={prediction.overallScore} size={92} colorClass={tone.ring} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-0.5">
              <TrendingUp size={14} className={tone.text} />
              <span className={`text-xs font-semibold uppercase tracking-wide ${tone.text}`}>
                {prediction.verdict || verdictFromScore(prediction.overallScore)}
              </span>
            </div>
            <p className="text-base text-slate-900 font-medium leading-snug">
              {prediction.summary}
            </p>
          </div>
        </div>
      </div>

      {/* Sub-scores */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <SubScoreCard icon={<ImageIcon size={16} />} label="Media" score={prediction.mediaScore} />
        <SubScoreCard icon={<TypeIcon size={16} />} label="Texto" score={prediction.captionScore} />
      </div>

      {/* Improvements */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-7 w-7 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center">
            <Lightbulb size={14} />
          </div>
          <h3 className="font-semibold text-slate-900">Cómo mejorar tu post</h3>
        </div>
        <ol className="space-y-2">
          {prediction.improvements.map((tip, i) => (
            <li key={i} className="flex gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
              <span className="flex-none h-6 w-6 rounded-full bg-brand-50 text-brand-700 text-xs font-bold flex items-center justify-center mt-0.5">
                {i + 1}
              </span>
              <span className="text-sm text-slate-700 leading-relaxed">{tip}</span>
            </li>
          ))}
        </ol>
      </div>

      {usage && <UsagePill usage={usage} className="mb-4" />}

      <div className="flex items-center justify-end gap-2">
        <button onClick={onRetry} className="btn-secondary inline-flex items-center gap-1.5">
          <Zap size={14} /> Analizar de nuevo
        </button>
        <button onClick={onClose} className="btn-primary">Listo</button>
      </div>
    </div>
  );
}

/* ─────────────────────────── Bits ─────────────────────────── */

function ScoreRing({ value, size, colorClass }: { value: number; size: number; colorClass: string }) {
  const stroke = 9;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.max(0, Math.min(100, value)) / 100) * circumference;
  return (
    <div className="relative flex-none" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="currentColor" strokeWidth={stroke} fill="none" className="text-slate-200" />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          stroke="currentColor" strokeWidth={stroke} fill="none"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          className={colorClass}
          style={{ transition: 'stroke-dashoffset 800ms cubic-bezier(0.16,1,0.3,1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold text-slate-900 tabular-nums">{value}</span>
      </div>
    </div>
  );
}

function SubScoreCard({ icon, label, score }: { icon: React.ReactNode; label: string; score: number }) {
  const tone = scoreTone(score);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
          <span className="text-slate-400">{icon}</span>
          {label}
        </div>
        <span className="text-sm font-bold text-slate-900 tabular-nums">{score}<span className="text-slate-400 text-xs">/100</span></span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full rounded-full ${tone.bar}`}
          style={{ width: `${score}%`, transition: 'width 800ms cubic-bezier(0.16,1,0.3,1)' }}
        />
      </div>
    </div>
  );
}

function SummaryCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-0.5">{icon} {label}</div>
      <div className="text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function BulletItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle2 size={14} className="text-brand-600 mt-0.5 flex-none" />
      <span>{children}</span>
    </li>
  );
}

function UsagePill({ usage, className = '' }: { usage: AiUsage; className?: string }) {
  const pct = usage.limit > 0 ? Math.min(100, (usage.used / usage.limit) * 100) : 0;
  return (
    <div className={`rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 ${className}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-600">
          {usage.planName && <span className="font-medium text-slate-700">Plan {usage.planName}</span>}
          {!usage.planName && <span className="text-slate-500">Sin suscripción</span>}
        </span>
        <span className="text-slate-900 font-medium tabular-nums">
          {usage.used} / {usage.limit} este mes
        </span>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-slate-200 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-brand-500 to-purple-600"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function UpgradeCta({ title, subtitle, onClose }: { title: string; subtitle: string; onClose: () => void }) {
  return (
    <div className="text-center py-4">
      <div className="mx-auto h-12 w-12 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 mb-3">
        <Lock size={20} />
      </div>
      <h3 className="font-semibold text-slate-900">{title}</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{subtitle}</p>
      <div className="mt-5 flex items-center justify-center gap-2">
        <Link to="/dashboard/billing" className="btn-primary">Ver planes</Link>
        <button onClick={onClose} className="btn-secondary">Cerrar</button>
      </div>
    </div>
  );
}

/* ───────────────── Helpers ───────────────── */

function scoreTone(score: number) {
  if (score >= 80) return {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
    ring: 'text-emerald-500',
    bar: 'bg-gradient-to-r from-emerald-400 to-emerald-600',
  };
  if (score >= 60) return {
    bg: 'bg-brand-50',
    border: 'border-brand-200',
    text: 'text-brand-700',
    ring: 'text-brand-500',
    bar: 'bg-gradient-to-r from-brand-400 to-brand-600',
  };
  if (score >= 40) return {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    ring: 'text-amber-500',
    bar: 'bg-gradient-to-r from-amber-400 to-amber-600',
  };
  return {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    ring: 'text-red-500',
    bar: 'bg-gradient-to-r from-red-400 to-red-600',
  };
}

function verdictFromScore(s: number) {
  if (s >= 85) return 'Excelente';
  if (s >= 70) return 'Muy bueno';
  if (s >= 55) return 'Bueno';
  if (s >= 40) return 'Mejorable';
  return 'Flojo';
}
